import React, { useEffect, useRef, useState } from 'react'
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BanIcon,
  CheckIcon,
  ChevronRightIcon,
  LinkIcon,
  MoreVerticalIcon,
  ShieldCheckIcon,
  TagIcon,
  Trash2Icon,
  Undo2Icon,
  UserCircleIcon,
} from 'lucide-react'
import type { ConversationAction, ConversationState } from '../../api/conversationActions'
import type { InboxAssignee, InboxFeedItem } from '../../types/inbox'
import { assigneeDisplayName } from '../../lib/inboxPermissions'

interface LifecycleItem {
  action: ConversationAction
  label: string
  icon: React.ElementType
  danger?: boolean
}

const LIFECYCLE_BY_STATE: Record<ConversationState, LifecycleItem[]> = {
  active: [
    { action: 'archive', label: 'Archive', icon: ArchiveIcon },
    { action: 'spam', label: 'Mark as spam', icon: BanIcon, danger: true },
    { action: 'delete', label: 'Move to trash', icon: Trash2Icon, danger: true },
  ],
  archived: [
    { action: 'unarchive', label: 'Unarchive', icon: ArchiveRestoreIcon },
    { action: 'spam', label: 'Mark as spam', icon: BanIcon, danger: true },
    { action: 'delete', label: 'Move to trash', icon: Trash2Icon, danger: true },
  ],
  spam: [
    { action: 'not_spam', label: 'Not spam', icon: ShieldCheckIcon },
    { action: 'delete', label: 'Move to trash', icon: Trash2Icon, danger: true },
  ],
  deleted: [{ action: 'restore', label: 'Restore', icon: Undo2Icon }],
}

export interface InboxListItemActionsMenuProps {
  item: InboxFeedItem
  assignees: InboxAssignee[]
  canAssignOthers: boolean
  currentUserId: string | null
  onLink: () => void
  onAssign: (userId: string | null) => void
  onLabel: () => void
  onResolve: () => void
  onLifecycleAction: (action: ConversationAction) => void
  className?: string
}

/** Unified overflow menu for every inbox list row (conversations and calls). */
export function InboxListItemActionsMenu({
  item,
  assignees,
  canAssignOthers,
  currentUserId,
  onLink,
  onAssign,
  onLabel,
  onResolve,
  onLifecycleAction,
  className = '',
}: InboxListItemActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      setAssignOpen(false)
      return
    }
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setAssignOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  const state = (item.state || 'active') as ConversationState
  const lifecycleItems = LIFECYCLE_BY_STATE[state] || LIFECYCLE_BY_STATE.active
  const assignOptions = canAssignOthers
    ? assignees
    : assignees.filter((a) => a.id === currentUserId)

  function closeMenu() {
    setOpen(false)
    setAssignOpen(false)
  }

  function menuBtn(label: string, icon: React.ElementType, onClick: () => void, danger?: boolean) {
    const Icon = icon
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
          danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    )
  }

  return (
    <div
      ref={rootRef}
      className={`relative flex-shrink-0 ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Actions"
        className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
      >
        <MoreVerticalIcon className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-1 max-h-[min(24rem,70vh)] overflow-y-auto overflow-x-hidden">
          {menuBtn('Link record', LinkIcon, () => {
            closeMenu()
            onLink()
          })}

          <div className="relative">
            <button
              type="button"
              onClick={() => setAssignOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">Assign to staff</span>
              <ChevronRightIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform ${assignOpen ? 'rotate-90' : ''}`} />
            </button>
            {assignOpen && (
              <div className="border-t border-gray-100 bg-gray-50/80 py-1 max-h-40 overflow-y-auto">
                {canAssignOthers && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu()
                      onAssign(null)
                    }}
                    className="w-full text-left px-4 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
                  >
                    Unassigned
                  </button>
                )}
                {currentUserId && !canAssignOthers && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu()
                      onAssign(currentUserId)
                    }}
                    className="w-full text-left px-4 py-1.5 text-xs text-blue-700 hover:bg-gray-100"
                  >
                    Claim to me
                  </button>
                )}
                {assignOptions.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      closeMenu()
                      onAssign(user.id)
                    }}
                    className={`w-full text-left px-4 py-1.5 text-xs hover:bg-gray-100 truncate ${
                      user.id === item.assignedToUserId ? 'text-blue-700 font-medium' : 'text-gray-800'
                    }`}
                  >
                    {assigneeDisplayName(user)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {menuBtn('Label', TagIcon, () => {
            closeMenu()
            onLabel()
          })}

          {item.handlingStatus !== 'resolved' &&
            menuBtn('Resolve', CheckIcon, () => {
              closeMenu()
              onResolve()
            })}

          <div className="my-1 border-t border-gray-100" />

          {lifecycleItems.map((entry) => {
            const Icon = entry.icon
            return (
              <button
                key={entry.action}
                type="button"
                onClick={() => {
                  closeMenu()
                  onLifecycleAction(entry.action)
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  entry.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{entry.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
